/**
 * The presentation pieces of the public room search (`/properties`), built to
 * the approved website draft's "3c WG-Zimmer Suche" screen.
 *
 * These are presentation only: every filter value, the result list, favourites
 * and the map still live in `pages/Properties.tsx`. Palette and copy rules come
 * from `lib/marketing-theme` — in particular, no turquoise anywhere here.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Search,
  ArrowRight,
  Heart,
  LayoutGrid,
  Map as MapIcon,
  Check,
} from "lucide-react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SmoothImage } from "@/components/ui/smooth-image";
import { getImageUrl, getImageSrcSet } from "@/lib/imageUrl";
import {
  INK,
  INK_DEEP,
  MUTED,
  MUTED_SOFT,
  SAND,
  LINE,
  LINE_SOFT,
  LINE_STRONG,
  IMAGE_BG,
  FIELD,
  EASE,
} from "@/lib/marketing-theme";
import type { PropertyRoom } from "./PropertyCard";

/**
 * Listing-card image sizing, carried over from the `PropertyCard` this grid
 * replaced. Without it `room.image` is the untouched original, so a 3000px
 * photo is downloaded for a 300px-tall card — once per result.
 *
 * `SmoothImage` already sets `loading="lazy"` and `decoding="async"`, so the
 * images below the fold were never eagerly fetched; what was missing is that
 * the ones that DO load were loading at full resolution.
 */
const CARD_WIDTH = 600;
const CARD_SRCSET = [400, 800];
const CARD_SIZES = "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw";

const fieldLabel = "text-[12px] font-semibold";

/**
 * Two-handle price range, drawn to the draft (4px rail, petrol fill, 12px
 * white thumbs with a petrol ring).
 *
 * Not `@/components/ui/slider`: that one renders a single `Thumb`, so a
 * two-value range got one handle and a bar that looked stuck at the floor.
 * Radix needs one Thumb per value.
 */
function RangeSlider({
  value,
  min,
  max,
  step = 10,
  onValueChange,
  label,
}: {
  value: [number, number];
  min: number;
  max: number;
  step?: number;
  onValueChange: (next: [number, number]) => void;
  label: string;
}) {
  return (
    <SliderPrimitive.Root
      value={value}
      min={min}
      max={max}
      step={step}
      minStepsBetweenThumbs={1}
      onValueChange={(next) =>
        onValueChange([next[0], next[1]] as [number, number])
      }
      // px-1.5 keeps both thumbs inside the column: at the very ends a 12px
      // handle centred on the rail would otherwise hang over the divider.
      className="relative mt-1.5 flex w-full touch-none select-none items-center px-1.5"
    >
      <SliderPrimitive.Track
        className="relative h-1 w-full grow rounded-full"
        style={{ background: LINE_SOFT }}
      >
        <SliderPrimitive.Range
          className="absolute h-full rounded-full"
          style={{ background: INK }}
        />
      </SliderPrimitive.Track>
      {[0, 1].map((i) => (
        <SliderPrimitive.Thumb
          key={i}
          aria-label={`${label} ${i === 0 ? "min" : "max"}`}
          className="block h-3 w-3 rounded-full bg-white outline-none transition-transform duration-200 hover:scale-125 focus-visible:scale-125"
          style={{ border: `2px solid ${INK}` }}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

/* ------------------------------------------------------------------ */
/* Filter bar                                                          */
/* ------------------------------------------------------------------ */

export type PriceRange = { min: string; max: string };

export function SearchFilterBar({
  de,
  cities,
  cityCounts,
  selectedCity,
  onCityChange,
  moveInDate,
  onMoveInDate,
  priceRange,
  onPriceRange,
  priceBounds,
  amenityOptions,
  amenityLabels,
  selectedAmenities,
  onAmenities,
  onSubmit,
}: {
  de: boolean;
  /** Public city list, "all" first. */
  cities: string[];
  cityCounts: Record<string, number>;
  selectedCity: string;
  onCityChange: (city: string) => void;
  moveInDate: string;
  onMoveInDate: (value: string) => void;
  priceRange: PriceRange;
  onPriceRange: (range: PriceRange) => void;
  /** [floor, ceiling] derived from the real listings. */
  priceBounds: [number, number];
  /** Keyed by a stable id so a language switch cannot orphan a selection. */
  amenityOptions: { value: string; label: string }[];
  /** Labels for every known amenity, so the summary survives filtering. */
  amenityLabels: Record<string, string>;
  /** Holds `amenityOptions[].value`, never the translated label. */
  selectedAmenities: string[];
  onAmenities: (next: string[]) => void;
  onSubmit: () => void;
}) {
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState("");
  const [floor, ceiling] = priceBounds;

  const cityLabel =
    selectedCity === "all" ? (de ? "Alle Städte" : "All cities") : selectedCity;

  const visibleCities = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    return cities.filter(
      (c) => !q || c === "all" || c.toLowerCase().includes(q)
    );
  }, [cities, cityQuery]);

  // The slider is the source of truth while dragging; the page keeps the
  // filter as strings so an empty value still means "no bound set".
  const low = priceRange.min ? Number(priceRange.min) : floor;
  const high = priceRange.max ? Number(priceRange.max) : ceiling;
  const priceTouched = Boolean(priceRange.min || priceRange.max);

  // The summary line shows labels, but the state holds values, so it has
  // to be resolved through the options rather than printed directly.
  const selectedLabels = selectedAmenities.map(
    (v) =>
      amenityLabels[v] ?? amenityOptions.find((o) => o.value === v)?.label ?? v
  );
  const amenityLabel = selectedLabels.length
    ? selectedLabels.slice(0, 2).join(", ") +
      (selectedLabels.length > 2 ? " …" : "")
    : de
      ? "Beliebig"
      : "Any";

  return (
    <div
      className="flex flex-col gap-2 rounded-[24px] border bg-white p-2 shadow-[0_10px_30px_rgba(9,46,44,0.07)] lg:grid lg:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:items-center lg:gap-0 lg:rounded-full"
      style={{ borderColor: LINE }}
    >
      {/* city */}
      <Popover open={cityOpen} onOpenChange={setCityOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-14 items-center gap-3 rounded-[16px] px-5 text-left lg:min-w-[210px] lg:rounded-full"
            style={{ background: FIELD }}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className={fieldLabel} style={{ color: MUTED_SOFT }}>
                {de ? "Stadt" : "City"}
              </span>
              <span
                className="truncate text-base font-semibold"
                style={{ color: INK }}
              >
                {cityLabel}
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform duration-300 ${
                cityOpen ? "rotate-180" : ""
              }`}
              style={{ color: INK }}
              strokeWidth={2.4}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={10}
          className="w-[320px] rounded-[14px] border p-3 shadow-[0_24px_60px_rgba(9,46,44,0.16)]"
          style={{ borderColor: LINE }}
        >
          <label
            className="mb-1.5 flex h-11 items-center gap-2.5 rounded-[10px] px-3.5"
            style={{ background: FIELD }}
          >
            <Search
              className="h-4 w-4 shrink-0"
              style={{ color: MUTED_SOFT }}
              strokeWidth={2.2}
            />
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              placeholder={de ? "Stadt suchen" : "Search city"}
              className="min-w-0 flex-1 bg-transparent text-[15px] outline-none"
              style={{ color: INK }}
            />
          </label>
          <div className="flex max-h-[260px] flex-col overflow-y-auto">
            {visibleCities.map((city) => {
              const on = city === selectedCity;
              const name =
                city === "all" ? (de ? "Alle Städte" : "All cities") : city;
              const count = cityCounts[city] ?? 0;
              return (
                <button
                  key={city}
                  type="button"
                  onClick={() => {
                    onCityChange(city);
                    setCityOpen(false);
                    setCityQuery("");
                  }}
                  className="flex items-center justify-between gap-3 rounded-[10px] px-3.5 py-2.5 text-left text-[15px] transition-colors"
                  style={{
                    background: on ? FIELD : "transparent",
                    color: INK,
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  <span className="truncate">{name}</span>
                  <span
                    className="shrink-0 text-[13px]"
                    style={{ color: MUTED_SOFT }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
            {visibleCities.length === 0 && (
              <p className="px-3.5 py-3 text-[14px]" style={{ color: MUTED }}>
                {de ? "Keine Stadt gefunden." : "No city found."}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* move-in */}
      <label
        className="flex flex-col justify-center gap-0.5 px-5 py-2 lg:ml-5 lg:border-l lg:py-0"
        style={{ borderColor: LINE_SOFT }}
      >
        <span className={fieldLabel} style={{ color: MUTED_SOFT }}>
          {de ? "Einzug ab" : "Move-in from"}
        </span>
        {/* A native date input shows "dd/mm/yyyy" when empty, which reads as a
            broken field next to the other three. The real input stays (it is
            the accessible control) and an overlay carries the draft's
            "Beliebig" until a date is actually chosen. */}
        <span className="relative block">
          <input
            type="date"
            value={moveInDate}
            onChange={(e) => onMoveInDate(e.target.value)}
            aria-label={de ? "Einzug ab" : "Move-in from"}
            className="w-full bg-transparent text-base font-semibold outline-none"
            style={{ color: moveInDate ? INK : "transparent" }}
          />
          {!moveInDate && (
            <span
              className="pointer-events-none absolute inset-y-0 left-0 flex items-center text-base font-semibold"
              style={{ color: MUTED }}
            >
              {de ? "Beliebig" : "Any"}
            </span>
          )}
        </span>
      </label>

      {/* price */}
      <div
        className="flex flex-col justify-center gap-0.5 px-5 py-2 lg:border-l lg:py-0"
        style={{ borderColor: LINE_SOFT }}
      >
        <span className={fieldLabel} style={{ color: MUTED_SOFT }}>
          {de ? "Preisspanne" : "Price range"}
        </span>
        <motion.span
          key={`${low}-${high}`}
          initial={{ opacity: 0.35 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25, ease: EASE }}
          className="text-base font-semibold"
          style={{ color: priceTouched ? INK : MUTED }}
        >
          {low} – {high} €
        </motion.span>
        <RangeSlider
          value={[low, high]}
          min={floor}
          max={ceiling}
          onValueChange={([nextLow, nextHigh]) =>
            onPriceRange({
              min: nextLow <= floor ? "" : String(nextLow),
              max: nextHigh >= ceiling ? "" : String(nextHigh),
            })
          }
          label={de ? "Preisspanne" : "Price range"}
        />
      </div>

      {/* amenities */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex flex-col justify-center gap-0.5 px-5 py-2 text-left lg:border-l lg:py-0"
            style={{ borderColor: LINE_SOFT }}
          >
            <span className={fieldLabel} style={{ color: MUTED_SOFT }}>
              {de ? "Ausstattung" : "Amenities"}
            </span>
            <span
              className="truncate text-base font-semibold"
              style={{ color: INK }}
            >
              {amenityLabel}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={10}
          className="w-[280px] rounded-[14px] border p-2 shadow-[0_24px_60px_rgba(9,46,44,0.16)]"
          style={{ borderColor: LINE }}
        >
          <div className="flex max-h-[300px] flex-col overflow-y-auto">
            {amenityOptions.length === 0 && (
              <p className="px-3 py-3 text-[14px]" style={{ color: MUTED }}>
                {de ? "Keine Angaben." : "Nothing listed yet."}
              </p>
            )}
            {amenityOptions.map((o) => {
              const on = selectedAmenities.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() =>
                    onAmenities(
                      on
                        ? selectedAmenities.filter((x) => x !== o.value)
                        : [...selectedAmenities, o.value]
                    )
                  }
                  className="flex items-center justify-between gap-3 rounded-[10px] px-3 py-2.5 text-left text-[15px]"
                  style={{ color: INK, fontWeight: on ? 600 : 500 }}
                >
                  <span className="truncate">{o.label}</span>
                  <span
                    className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border"
                    style={{
                      borderColor: on ? INK : LINE,
                      background: on ? INK : "transparent",
                    }}
                  >
                    {on && (
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        onClick={onSubmit}
        className="inline-flex h-[52px] shrink-0 items-center justify-center gap-2 rounded-full px-7 text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
        style={{ background: INK }}
      >
        <Search className="h-4 w-4" strokeWidth={2.5} />
        {de ? "Suchen" : "Search"}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Results header                                                      */
/* ------------------------------------------------------------------ */

export function ResultsHeader({
  de,
  count,
  availableOnly,
  onAvailableOnly,
  sortLabel,
  sortControl,
  viewMode,
  onViewMode,
}: {
  de: boolean;
  count: number;
  availableOnly: boolean;
  onAvailableOnly: (next: boolean) => void;
  sortLabel: string;
  sortControl?: React.ReactNode;
  viewMode: "grid" | "map";
  onViewMode: (mode: "grid" | "map") => void;
}) {
  return (
    <div
      className="flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-center sm:justify-between"
      style={{ borderColor: LINE_STRONG }}
    >
      <span className="text-[17px] font-semibold" style={{ color: INK }}>
        {count}{" "}
        {de
          ? count === 1
            ? "Zimmer"
            : "Zimmer"
          : count === 1
            ? "room"
            : "rooms"}
      </span>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <button
          type="button"
          onClick={() => onAvailableOnly(!availableOnly)}
          role="switch"
          aria-checked={availableOnly}
          className="inline-flex items-center gap-2.5 text-[15px] font-semibold"
          style={{ color: INK }}
        >
          <span
            className="relative flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300"
            style={{ background: availableOnly ? INK : "#DCD7CC" }}
          >
            <motion.span
              layout
              transition={{ duration: 0.25, ease: EASE }}
              className="absolute h-5 w-5 rounded-full bg-white shadow-sm"
              style={{ left: availableOnly ? 22 : 2 }}
            />
          </span>
          {de ? "Nur verfügbare Zimmer" : "Available only"}
        </button>

        {sortControl ?? (
          <span className="text-[15px]" style={{ color: MUTED }}>
            {sortLabel}
          </span>
        )}

        <div
          className="inline-flex items-center gap-0.5 rounded-full p-[3px]"
          style={{ background: LINE_SOFT }}
        >
          {[
            {
              key: "grid" as const,
              icon: LayoutGrid,
              label: de ? "Liste" : "List",
            },
            { key: "map" as const, icon: MapIcon, label: de ? "Karte" : "Map" },
          ].map(({ key, icon: Icon, label }) => {
            const on = viewMode === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onViewMode(key)}
                aria-pressed={on}
                className="relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-[14.5px] font-semibold transition-colors"
                style={{ color: on ? INK : MUTED }}
              >
                {on && (
                  <motion.span
                    layoutId="view-toggle"
                    transition={{ duration: 0.3, ease: EASE }}
                    className="absolute inset-0 rounded-full bg-white shadow-sm"
                  />
                )}
                <Icon
                  className="relative h-[15px] w-[15px]"
                  strokeWidth={2.4}
                />
                <span className="relative">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Result card                                                         */
/* ------------------------------------------------------------------ */

export function RoomResultCard({
  room,
  detail,
  availability,
  index,
  isFavorited,
  onFavoriteClick,
  de,
}: {
  room: PropertyRoom;
  /** "14 m² · Bett 140 cm · Schreibtisch" — built from the room's amenities. */
  detail: string;
  /** "Verfügbar ab 01.11." or "Warteliste". */
  availability: string;
  index: number;
  isFavorited: boolean;
  onFavoriteClick: (e: React.MouseEvent) => void;
  de: boolean;
}) {
  const [burst, setBurst] = useState(0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.07, ease: EASE }}
    >
      <Link
        to={`/property/${room.roomId}`}
        className="group flex min-w-0 flex-col gap-4"
        style={{ color: INK }}
      >
        <div
          className="relative h-[240px] overflow-hidden rounded-[14px] sm:h-[300px]"
          style={{ background: IMAGE_BG }}
        >
          <SmoothImage
            src={getImageUrl(room.image, { width: CARD_WIDTH }) ?? room.image}
            srcSet={getImageSrcSet(room.image, CARD_SRCSET)}
            sizes={CARD_SIZES}
            alt={room.title}
            wrapperClassName="absolute inset-0 h-full w-full"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
          />
          <span
            className="absolute left-5 top-5 inline-flex h-[30px] items-center rounded-full px-3.5 text-[13.5px] font-semibold"
            style={{
              background: room.available ? "#FFFFFF" : "#F2EBE0",
              color: room.available ? INK : MUTED,
            }}
          >
            {availability}
          </span>
          <motion.button
            type="button"
            onClick={(e) => {
              // The ring only fires on the way IN: an un-like that burst
              // outward would read as "saved", the opposite of what happened.
              if (!isFavorited) setBurst((b) => b + 1);
              onFavoriteClick(e);
            }}
            aria-label={
              isFavorited
                ? de
                  ? "Aus Favoriten entfernen"
                  : "Remove from favourites"
                : de
                  ? "Zu Favoriten hinzufügen"
                  : "Add to favourites"
            }
            aria-pressed={isFavorited}
            whileTap={{ scale: 0.82 }}
            whileHover={{ scale: 1.12 }}
            transition={{ type: "spring", stiffness: 520, damping: 18 }}
            className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-white/90 backdrop-blur"
          >
            <AnimatePresence>
              {burst > 0 && (
                <motion.span
                  key={burst}
                  initial={{ scale: 0.7, opacity: 0.6 }}
                  animate={{ scale: 2.1, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.55, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{ border: `2px solid ${INK}` }}
                />
              )}
            </AnimatePresence>
            <motion.span
              key={isFavorited ? "on" : "off"}
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 600, damping: 13 }}
              className="relative grid place-items-center"
            >
              <Heart
                className="h-4 w-4"
                style={{ color: INK }}
                fill={isFavorited ? INK : "none"}
                strokeWidth={2}
              />
            </motion.span>
          </motion.button>
        </div>

        <div className="flex flex-col gap-1">
          {room.city && (
            <span
              className="text-[13px] font-semibold"
              style={{ color: MUTED_SOFT }}
            >
              {room.city}
            </span>
          )}
          <div className="flex items-baseline justify-between gap-4">
            <span
              className="min-w-0 text-[19px] font-semibold sm:text-[21px]"
              style={{ letterSpacing: "-0.03em", color: INK }}
            >
              {room.buildingName
                ? `${room.buildingName} · ${room.title}`
                : room.title}
            </span>
            <span
              className="shrink-0 text-[17px] font-semibold"
              style={{ color: INK }}
            >
              {room.price > 0
                ? `${room.price} €`
                : de
                  ? "auf Anfrage"
                  : "on request"}
            </span>
          </div>
          {detail && (
            <span className="text-[15px]" style={{ color: MUTED }}>
              {detail}
            </span>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state — "coming to your city"                                 */
/* ------------------------------------------------------------------ */

export function WaitlistBand({
  de,
  city,
  onClear,
  hasFilters,
}: {
  de: boolean;
  /** The chosen city, or null when the search was across all of them. */
  city: string | null;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="flex flex-col items-start justify-between gap-6 rounded-[14px] border px-8 py-10 sm:flex-row sm:items-center sm:px-11 sm:py-12"
      style={{ background: SAND, borderColor: LINE }}
    >
      <div className="flex flex-col gap-2">
        <span
          className="text-[24px] font-semibold leading-[1.1] sm:text-[28px]"
          style={{ letterSpacing: "-0.03em", color: INK }}
        >
          {city
            ? de
              ? `Bald auch in ${city}.`
              : `Coming to ${city} soon.`
            : de
              ? "Gerade ist nichts frei."
              : "Nothing free right now."}
        </span>
        <span
          className="max-w-[46ch] text-[16px] leading-[1.55]"
          style={{ color: MUTED }}
        >
          {de
            ? "Trag dich ein, dann melden wir uns, sobald hier die erste WG startet."
            : "Leave your email and we will get in touch as soon as the first shared flat opens here."}
        </span>
      </div>
      <div className="flex shrink-0 flex-wrap gap-3">
        {hasFilters && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-11 items-center rounded-full border px-5 text-[14.5px] font-semibold"
            style={{ borderColor: LINE, color: INK }}
          >
            {de ? "Filter zurücksetzen" : "Clear filters"}
          </button>
        )}
        <Link
          to="/contact"
          className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-[14.5px] font-semibold text-white"
          style={{ background: INK }}
        >
          {de ? "Auf die Warteliste" : "Join the waiting list"}
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </Link>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

const TENANT_FAQ: {
  q: { de: string; en: string };
  a: { de: string; en: string };
}[] = [
  {
    q: {
      de: "Was ist in der Miete enthalten?",
      en: "What is included in the rent?",
    },
    a: {
      de: "Möbliertes Zimmer, voll ausgestattete Küche, Nebenkosten, Strom, Heizung und WLAN. Was genau dazugehört, steht bei jedem Zimmer — ohne Nachzahlung am Jahresende.",
      en: "A furnished room, a fully equipped kitchen, utilities, electricity, heating and Wi-Fi. Exactly what is included is listed on every room — with no year-end bill to settle.",
    },
  },
  {
    q: {
      de: "Wie lange muss ich mindestens bleiben?",
      en: "How long do I have to stay?",
    },
    a: {
      de: "Die Mindestmietdauer beträgt 6 Monate. Danach kannst du mit der gesetzlichen Frist von 3 Monaten kündigen — direkt in der App.",
      en: "The minimum stay is 6 months. After that you can give notice at the statutory 3 months — straight from the app.",
    },
  },
  {
    q: { de: "Wie hoch ist die Kaution?", en: "How much is the deposit?" },
    a: {
      de: "2 Monatsmieten. Sie wird separat geführt und nach deinem Auszug abgerechnet.",
      en: "2 months' rent. It is held separately and settled after you move out.",
    },
  },
  {
    q: {
      de: "Welche Unterlagen brauche ich für die Bewerbung?",
      en: "What documents do I need to apply?",
    },
    a: {
      de: "Ausweis und einen Nachweis, dass du die Miete zahlen kannst — z. B. Gehaltsnachweis, Stipendium oder Bürgschaft. Alles lädst du im Bewerbungsprofil hoch.",
      en: "ID and proof that you can pay the rent — a payslip, a scholarship or a guarantee, for example. You upload all of it in your application profile.",
    },
  },
  {
    q: {
      de: "Kann ich das Zimmer vorher ansehen?",
      en: "Can I view the room first?",
    },
    a: {
      de: "Ja — per 3D-Rundgang jederzeit online, direkt beim Zimmer. Vor Ort gern nach Absprache.",
      en: "Yes — a 3D tour is online at any time, right on the room. In person by arrangement.",
    },
  },
  {
    q: {
      de: "Kann ich mich an der Adresse anmelden?",
      en: "Can I register at the address?",
    },
    a: {
      de: "Ja. Die Wohnungsgeberbestätigung bekommst du automatisch zum Einzug in der App.",
      en: "Yes. You get the landlord confirmation automatically in the app when you move in.",
    },
  },
  {
    q: { de: "Wer wohnt in meiner WG?", en: "Who lives in my shared flat?" },
    a: {
      de: "Schon vor dem Einzug siehst du in der App, wer mit dir wohnt, und kannst im WG-Chat Hallo sagen.",
      en: "Before you even move in you can see in the app who you will live with, and say hello in the flat chat.",
    },
  },
];

export function TenantFaq({ de }: { de: boolean }) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
    >
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]"
          style={{ letterSpacing: "-0.04em", color: INK }}
        >
          {de ? "Häufige Fragen." : "Frequently asked."}
        </motion.h2>

        <div className="flex flex-col">
          {TENANT_FAQ.map((item, i) => {
            const on = open === i;
            return (
              <motion.div
                key={item.q.en}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.5,
                  delay: Math.min(i, 4) * 0.05,
                  ease: EASE,
                }}
                className="border-t"
                style={{ borderColor: LINE_STRONG }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(on ? null : i)}
                  aria-expanded={on}
                  className="flex w-full items-center justify-between gap-5 py-6 text-left"
                >
                  <span
                    className="text-[17px] font-semibold sm:text-[19px]"
                    style={{ letterSpacing: "-0.02em", color: INK }}
                  >
                    {de ? item.q.de : item.q.en}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 transition-transform duration-300 ${
                      on ? "rotate-180" : ""
                    }`}
                    style={{ color: INK }}
                    strokeWidth={2.2}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {on && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p
                        className="max-w-[62ch] pb-6 text-[15.5px] leading-[1.6] sm:text-[16px]"
                        style={{ color: MUTED }}
                      >
                        {de ? item.a.de : item.a.en}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Closing band — "nothing for you?"                                   */
/* ------------------------------------------------------------------ */

export function NotifyBand({ de }: { de: boolean }) {
  const [email, setEmail] = useState("");
  const subject = de
    ? "Benachrichtigung über freie Zimmer"
    : "Notify me about free rooms";

  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: INK_DEEP }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mx-auto grid max-w-[1328px] items-end gap-10 lg:grid-cols-2 lg:gap-24"
      >
        <div className="flex flex-col gap-5">
          <h2
            className="font-display text-[2rem] font-semibold leading-[1.06] text-white sm:text-[2.5rem]"
            style={{ letterSpacing: "-0.04em" }}
          >
            {de ? "Nichts Passendes dabei?" : "Nothing quite right?"}
          </h2>
          <p
            className="max-w-[460px] text-[16px] leading-[1.6] sm:text-[17.5px]"
            style={{ color: "rgba(247,243,236,0.72)" }}
          >
            {de
              ? "Wir sagen dir Bescheid, sobald in deiner Stadt ein Zimmer frei wird."
              : "We will let you know as soon as a room opens up in your city."}
          </p>
        </div>

        {/* Mails the team rather than pretending to store a subscription:
            there is no waiting-list table yet, and a form that silently
            drops the address would be worse than an honest mailto. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            window.location.href = `mailto:kontakt@coliviq.de?subject=${encodeURIComponent(
              subject
            )}&body=${encodeURIComponent(email)}`;
          }}
          className="flex w-full items-center gap-2 rounded-full border p-2 pl-6"
          style={{ borderColor: "rgba(255,255,255,0.22)" }}
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={de ? "deine@email.de" : "you@email.com"}
            aria-label={de ? "E-Mail-Adresse" : "Email address"}
            className="min-w-0 flex-1 bg-transparent text-[16px] text-white outline-none placeholder:text-white/45"
          />
          <button
            type="submit"
            className="inline-flex h-12 shrink-0 items-center rounded-full bg-white px-6 text-[15px] font-semibold transition-transform duration-300 hover:scale-[1.03]"
            style={{ color: INK_DEEP }}
          >
            {de ? "Benachrichtigen" : "Notify me"}
          </button>
        </form>
      </motion.div>
    </section>
  );
}
